// ---------------------------------------------------------------------------
// An accessible dialog.
//
// What "accessible" means here, concretely:
//   * focus moves into the dialog when it opens and returns to whatever opened
//     it when it closes,
//   * Tab is trapped inside, so a keyboard user cannot wander into the page
//     behind and get lost,
//   * Escape closes it - the TOPMOST one, when dialogs are stacked,
//   * the page behind does not scroll,
//   * the browser is told it is a modal dialog with a title.
//
// ONE WAY OUT, FOUR DOORS. Clicking the dimmed backdrop, pressing Escape,
// the X in the corner and a Cancel button that asks for it all go through
// the same `requestClose`. That is what lets the dialog keep ONE promise
// everywhere: a form with unsaved work is never thrown away by a stray
// click - it asks first, in the same words, whichever door was used - and
// a save that is already running is never interrupted into a half state.
// A dialog that has nothing to lose closes at once.
//
// TWO THINGS IT GOT WRONG, AND WHY THEY ARE WRITTEN THE WAY THEY ARE NOW.
//
// FIRST: focus was initialised in an effect that depended on `[open, onClose]`.
// Almost every caller writes `onClose={() => setOpen(false)}`, which is a NEW
// function on every render of the page holding the dialog - and a controlled
// input re-renders that page on every keystroke. So React saw the dependency
// change, ran the cleanup (handing focus back to whatever opened the dialog)
// and re-ran the effect (focusing the first element in the panel). Once per
// character. Products → Add product ate every keystroke after the first and
// left the caret on the Close button.
//
// The dependency is now `[open]` alone, so focus is set up exactly once, on
// the closed → open transition. `onClose` is read through a ref, which is the
// standard way to let a long-lived listener see the latest callback without
// making the effect depend on its identity. It deliberately does NOT rely on
// callers remembering to wrap fifty inline arrows in `useCallback`: a rule
// nobody can see being broken is a rule that will be broken.
//
// SECOND: it focused "the first focusable element in the panel", and the panel
// begins with the header, and the header ends with the Close X. So every
// data-entry dialog opened with the caret on the button that throws the work
// away. Initial focus now follows a policy - the first real field in the body,
// falling back outwards - and the X is simply never a candidate. It stays in
// the tab order; it is just not where a person is put.
// ---------------------------------------------------------------------------

import { useEffect, useId, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';
import { onBackdropClick, useOverlayDismiss } from './overlayStack';

/// What a footer gets when it is a function: the guarded close, for a
/// Cancel button that should ask about unsaved work the way the X does.
export interface ModalFooterApi {
  requestClose: () => void;
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /// Buttons, rendered in a bar along the bottom. A function receives
  /// `requestClose`, so Cancel can go through the unsaved-changes guard.
  footer?: ReactNode | ((api: ModalFooterApi) => ReactNode);
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /// The caller KNOWS whether there is unsaved work. When given, it decides
  /// whether closing asks first.
  dirty?: boolean;
  /// The caller does not track dirtiness: ask before closing if anything
  /// inside the dialog was changed since it opened. A search box does not
  /// count - typing in one is looking, not editing.
  guardUnsaved?: boolean;
  /// A save is in flight: closing is ignored until it settles, so a click
  /// outside cannot leave the screen half-finished behind a request that
  /// will still land.
  busy?: boolean;
  /// Where the caret goes when this dialog opens, when the default policy
  /// picks the wrong thing.
  ///
  /// The default is right for almost everything, so this exists for the
  /// exception rather than as the usual way of doing it. Marking an element
  /// with `data-autofocus` does the same thing from inside the content, which
  /// is usually tidier than threading a ref through.
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
} as const;

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/// The things a person came here to fill in.
const FIELDS =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])';

/// Can a person actually reach this, or is it hidden behind a closed section?
///
/// Deliberately NOT `offsetParent !== null`. That reads the layout, and there
/// are two environments here: a browser, where it works, and jsdom, which
/// never lays anything out and reports `null` for absolutely everything. Under
/// the layout test the focus policy silently matched nothing in tests and fell
/// all the way through to the panel - which is exactly the kind of difference
/// between test and reality that makes a passing suite worthless.
///
/// Computed style answers the same question without needing a layout, and a
/// `display: none` ancestor does not show up on the child, so the walk goes up.
function isReachable(element: HTMLElement): boolean {
  if (element.hasAttribute('hidden') || element.closest('[hidden]')) return false;

  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }

  return true;
}

function firstReachable(root: Element | null, selector: string): HTMLElement | null {
  if (!root) return null;
  return [...root.querySelectorAll<HTMLElement>(selector)].find(isReachable) ?? null;
}

/// Where the caret goes when a dialog opens.
///
/// ONE POLICY, in one place, rather than a `.focus()` call scattered through
/// every feature that happens to have a form in it:
///
///   1. whatever the caller explicitly asked for,
///   2. anything inside the content marked `data-autofocus`,
///   3. the first real field in the body - which is what a form dialog wants,
///   4. failing that, the first thing in the body that can be focused at all,
///   5. failing that, the first button in the footer - which is what an
///      information dialog and a confirmation want, and which is the SAFE
///      action, because Cancel is rendered before Confirm,
///   6. failing everything, the panel itself, so focus is at least inside.
///
/// The Close X is not on this list at any point. It is in the header, which is
/// searched by none of these steps.
function initialTarget(
  panel: HTMLElement | null,
  body: HTMLElement | null,
  footer: HTMLElement | null,
  explicit: HTMLElement | null,
): HTMLElement | null {
  if (explicit && isReachable(explicit)) return explicit;

  return (
    firstReachable(panel, '[data-autofocus]') ??
    firstReachable(body, FIELDS) ??
    firstReachable(body, FOCUSABLE) ??
    firstReachable(footer, FOCUSABLE) ??
    panel
  );
}

/// Did this change come from a field whose contents are work, rather than
/// a search box somebody typed into to find something?
function isEditingChange(event: SyntheticEvent): boolean {
  const target = event.target as HTMLElement | null;
  if (!target) return false;
  if (target instanceof HTMLInputElement && target.type === 'search') return false;
  if (target.closest('[data-not-dirty]')) return false;
  return true;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dirty,
  guardUnsaved = false,
  busy = false,
  initialFocusRef,
}: ModalProps) {
  const { t } = useI18n();
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // The latest `onClose`, without the effects below depending on its identity.
  //
  // This is the whole fix for the keystroke bug. The listeners have to call
  // the CURRENT callback, but the effect must not be torn down and rebuilt
  // every time the parent re-renders and hands over a new arrow function -
  // because rebuilding it is what re-ran the focus initialisation, on every
  // character.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  // Whether anything inside has been changed since the dialog opened. Only
  // consulted when the caller asked for the guard without tracking dirtiness
  // itself.
  const touched = useRef(false);

  // The unsaved-changes question, when a close was asked for and there is
  // work to lose.
  const [guarding, setGuarding] = useState(false);

  /// The ONE way out. Every door - backdrop, Escape, X, a Cancel that asks
  /// for it - comes through here, so the promise is the same at each.
  const requestClose = () => {
    if (busy) return;
    const hasUnsavedWork = dirty ?? (guardUnsaved && touched.current);
    if (hasUnsavedWork) {
      setGuarding(true);
      return;
    }
    closeRef.current();
  };

  // Escape, for the topmost dialog only.
  useOverlayDismiss(open, requestClose);

  useEffect(() => {
    if (!open) {
      setGuarding(false);
      return;
    }

    touched.current = false;

    // Remember where focus was, so it can be handed back on close.
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    initialTarget(
      panel,
      bodyRef.current,
      footerRef.current,
      initialFocusRef?.current ?? null,
    )?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !panel) return;

      // Trap Tab inside the dialog by wrapping at both ends.
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(isReachable);
      if (focusable.length === 0) return;

      const firstElement = focusable[0]!;
      const lastElement = focusable[focusable.length - 1]!;

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    // Stop the page behind scrolling under the dialog.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;

      // Only ever on the way out - `open` going false, or the dialog
      // unmounting. It used to run on every parent render as well, which is
      // half of why typing threw focus around.
      //
      // Guarded, because the element that opened the dialog may have gone: a
      // row deleted, a list refreshed, a page navigated away from. Focusing a
      // node that is no longer in the document sends focus to `body`, which is
      // worse than leaving it alone.
      const returnTo = previouslyFocused.current;
      if (returnTo && returnTo.isConnected) returnTo.focus();
    };
    // `onClose` and `initialFocusRef` are deliberately absent. This effect
    // sets up focus for the LIFETIME of an open dialog; re-running it because
    // the parent re-rendered is the bug, not the feature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const footerContent = typeof footer === 'function' ? footer({ requestClose }) : footer;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[90] flex items-end justify-center p-0 sm:items-center sm:p-4">
        <div
          className="absolute inset-0 bg-charcoal/50"
          onClick={onBackdropClick(requestClose)}
          data-testid="modal-backdrop"
          aria-hidden="true"
        />

        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          aria-busy={busy || undefined}
          tabIndex={-1}
          onChangeCapture={(event) => {
            if (isEditingChange(event)) touched.current = true;
          }}
          className={clsx(
            'relative flex max-h-[92vh] w-full flex-col rounded-t-card bg-surface shadow-raised sm:rounded-card',
            SIZES[size],
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <h2 id={titleId} className="text-base font-semibold text-ink">
                {title}
              </h2>
              {description ? (
                <p id={descriptionId} className="mt-0.5 text-sm text-ink-muted">
                  {description}
                </p>
              ) : null}
            </div>

            <button
              type="button"
              onClick={requestClose}
              className="-me-1.5 -mt-1 shrink-0 rounded-control p-1.5 text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
              aria-label={t.common.close}
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div
            ref={bodyRef}
            className="scrollbar-brand min-h-0 flex-1 overflow-y-auto px-5 py-4"
          >
            {children}
          </div>

          {footerContent ? (
            <div
              ref={footerRef}
              className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-soft px-5 py-3.5"
            >
              {footerContent}
            </div>
          ) : null}
        </div>
      </div>

      {/* The question, asked in the same words whichever door was used.
          Its own dialog on top of this one: Escape or its backdrop answers
          "keep editing", and the editor underneath stays exactly as it was. */}
      <ConfirmDialog
        open={guarding}
        title={t.overlay.unsavedTitle}
        message={t.overlay.unsavedBody}
        confirmLabel={t.overlay.discard}
        cancelLabel={t.overlay.keepEditing}
        destructive
        onConfirm={() => {
          setGuarding(false);
          closeRef.current();
        }}
        onCancel={() => setGuarding(false)}
      />
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  /// Burgundy button for anything that removes or reverses.
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? 'btn-danger' : 'btn-primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm text-ink">{message}</p>
    </Modal>
  );
}
